import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { StopwatchJson } from '@core/models/built/session-step-value-json.model';
import { SessionStepValueType } from '@core/models/built/session-step-value-type.enum';
import { SessionStep, SessionStepValue } from '@core/models/session.model';
import { filterTruthy } from '@utils';
import { DateTime, Duration } from 'luxon';
import { BehaviorSubject, Observable, Subscription, timer } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
  templateUrl: './input-stopwatch-session.component.html',
  styles: [`
    .text-size-5 {
      font-size: 5rem;
      line-height: 5rem;
    }
    .icon-size-3 {
      font-size: 3rem;
      line-height: 1.5rem;
      width: auto;
    }
  `]
})
export class InputStopwatchSessionComponent implements OnInit, OnChanges, OnDestroy {

  @Input() step!: SessionStep;

  @Output() addValue = new EventEmitter<SessionStepValue>();
  @Output() commitAndNext = new EventEmitter<SessionStep>();

  private step$$ = new BehaviorSubject<SessionStep | undefined>(undefined);
  step$ = this.step$$.pipe(filterTruthy());

  private isRunning$$ = new BehaviorSubject<boolean>(false);
  isRunning$ = this.isRunning$$.asObservable();

  private timer$ = timer(0, 1000);
  private timerSubs = new Subscription();

  private durationMs$$ = new BehaviorSubject<number>(0);

  hours$ = this.durationMs$$.pipe(map(ms => Math.floor(Duration.fromMillis(ms).as('hours')).toString().padStart(2, '0')));
  minutes$ = this.durationMs$$.pipe(map(ms => Math.floor(Duration.fromMillis(ms).as('minutes') % 60).toString().padStart(2, '0')));
  seconds$ = this.durationMs$$.pipe(map(ms => Math.floor(Duration.fromMillis(ms).as('seconds') % 60).toString().padStart(2, '0')));

  private prevValue$: Observable<SessionStepValue | undefined> = this.step$.pipe(
    map(step => step.values ? this.orderValuesByTimestampAsc(step.values) : []),
    map(orderedValues => orderedValues[orderedValues.length - 1])
  );

  inputForm = new FormGroup({});

  private subs = new Subscription();

  constructor() { }

  ngOnInit() {
    this.subs.add(this.prevValue$
      .subscribe(prev => {
        let durationMs: number;
        const prevValue = prev ? prev.value as StopwatchJson : undefined;
        if (prevValue) {
          if (prevValue.type === 'start') {
            const ts = DateTime.fromISO(prev?.timestamp as string);
            const now = DateTime.local();
            const diff = now.diff(ts);
            durationMs = prevValue.durationFromStepStart + diff.as('milliseconds');
          } else {
            durationMs = prevValue.durationFromStepStart;
          }
        } else {
          durationMs = 0;
        }
        this.durationMs$$.next(durationMs);

        const isRunning = prevValue?.type === 'start';
        this.isRunning$$.next(isRunning);
        if (isRunning) {
          this.timerSubs = this.timer$
            .subscribe(s => {
              const duration = this.durationMs$$.getValue();
              this.durationMs$$.next(duration + 1000);
            });
        } else {
          this.timerSubs.unsubscribe();
        }
      })
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.step) {
      const step: SessionStep = changes.step.currentValue;
      this.step$$.next(step);
    }
  }

  ngOnDestroy() {
    this.timerSubs.unsubscribe();
    this.subs.unsubscribe();
  }

  start(step: SessionStep) {
    const value: SessionStepValue = {
      workflowId: step.workflowId,
      workflowStepId: step.workflowStepId,
      sessionId: step.sessionId,
      timestamp: DateTime.local().toJSDate(),
      type: SessionStepValueType.stopwatch,
      value: {
        type: 'start',
        durationFromStepStart: this.durationMs$$.getValue()
      }
    };
    this.addValue.emit(value);
  }

  stop(step: SessionStep) {
    const value: SessionStepValue = {
      workflowId: step.workflowId,
      workflowStepId: step.workflowStepId,
      sessionId: step.sessionId,
      timestamp: DateTime.local().toJSDate(),
      type: SessionStepValueType.stopwatch,
      value: {
        type: 'stop',
        durationFromStepStart: this.durationMs$$.getValue()
      }
    };
    this.addValue.emit(value);
  }

  submit(step: SessionStep) {
    if (step.values && step.values.length > 0) {
      this.timerSubs.unsubscribe();
      const now = DateTime.local().toJSDate();
      let values = step.values ? [...step.values] : [];
      if (this.isRunning$$.getValue()) {
        const value: SessionStepValue = {
          workflowId: step.workflowId,
          workflowStepId: step.workflowStepId,
          sessionId: step.sessionId,
          timestamp: now,
          type: SessionStepValueType.stopwatch,
          value: {
            type: 'stop',
            durationFromStepStart: this.durationMs$$.getValue()
          }
        };
        values = [...values, value];
      }
      this.commitAndNext.emit({ ...step, values, completedOn: now });
    }
  }

  private orderValuesByTimestampAsc(values: SessionStepValue[]): SessionStepValue[] {
    const ordered = [...values].sort((a, b) => {
      if (DateTime.fromJSDate(new Date(a.timestamp)) < DateTime.fromJSDate(new Date(b.timestamp))) {
        return -1;
      }
      if (DateTime.fromJSDate(new Date(a.timestamp)) > DateTime.fromJSDate(new Date(b.timestamp))) {
        return 1;
      }
      return 0;
    }) || [];
    return ordered;
  }
}
